/**
 * BridgeClubPromptModal — a workflow nudge shown at the till when the
 * cashier clicks Pay on an UNATTRIBUTED transaction (no BC member, no
 * customer). Goal: stop missed Bridge Club signup opportunities.
 *
 * Deliberately NOT a signup form. It routes into the EXISTING Add
 * Customer / Customer Lookup modal (the one Charles already uses) via
 * the onLookup callback. Three exits:
 *   - onLookup → open the existing customer lookup/add flow
 *   - onSkip   → log the skip + proceed to payment
 *   - onCancel → back to cart, no payment, nothing logged
 *
 * Self-contained inline styles (matches StorePOS's inline-style
 * convention); no external CSS dependency.
 */
import React from 'react';

interface Props {
  isOpen: boolean;
  onLookup: () => void;
  onSkip: () => void;
  onCancel: () => void;
}

const styles: { [k: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3000, padding: '1rem',
  },
  modal: {
    background: 'white', borderRadius: 12, width: '100%', maxWidth: 440,
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
  },
  header: {
    padding: '1.25rem 1.5rem 0.5rem', fontSize: '1.25rem', fontWeight: 700,
    color: '#111827',
  },
  body: { padding: '0 1.5rem 1rem', color: '#374151', fontSize: '0.95rem', lineHeight: 1.5 },
  actions: { display: 'flex', flexDirection: 'column', gap: '0.625rem', padding: '0 1.5rem 1.5rem' },
  btnPrimary: {
    background: '#059669', color: 'white', border: 'none', borderRadius: 8,
    padding: '0.875rem 1rem', fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
    minHeight: 48,
  },
  btnSecondary: {
    background: 'white', color: '#374151', border: '1px solid #d1d5db',
    borderRadius: 8, padding: '0.875rem 1rem', fontSize: '1rem', fontWeight: 600,
    cursor: 'pointer', minHeight: 48,
  },
  btnGhost: {
    background: 'transparent', color: '#6b7280', border: 'none',
    padding: '0.625rem', fontSize: '0.9rem', cursor: 'pointer', minHeight: 44,
  },
};

const BridgeClubPromptModal: React.FC<Props> = ({ isOpen, onLookup, onSkip, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div
      style={styles.overlay}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Bridge Club member prompt"
    >
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>Bridge Club Member?</div>
        <div style={styles.body}>
          Bridge Club members earn <strong>$10 off every $500</strong> in feed
          purchases. Look up an existing member or add a new one — or skip if
          this is just a walk-in.
        </div>
        <div style={styles.actions}>
          <button type="button" style={styles.btnPrimary} onClick={onLookup}>
            Look Up / Add Customer
          </button>
          <button type="button" style={styles.btnSecondary} onClick={onSkip}>
            Skip — Just a Walk-in
          </button>
          <button type="button" style={styles.btnGhost} onClick={onCancel}>
            Cancel — Back to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubPromptModal;
